import { useEffect, useMemo, useRef, useState } from 'react';
import { useRollbar } from '@rollbar/react';
import { Embed, models } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';

import { Report } from '@/api/types';

import { WorkspaceAppPath } from '../../constants/workspaceNames';
import { extractVisualDataWithReportDetails, fireHeapEvent } from '../../utils/heapUtil';
import { AppLoader, PreqinLogo } from '../Loader/AppLoader';

import styles from './PowerBiReport.module.scss';

export const PowerBiReport = ({
  report,
  workspaceName,
  accessToken,
  workspacePath,
}: {
  report: Report;
  workspaceName: string;
  accessToken: string;
  workspacePath: string;
}) => {
  const rollbar = useRollbar();
  const [showPreqinLogo, setShowPreqinLogo] = useState<boolean>(true);
  const isInitRenderEventSent = useRef(false);

  useEffect(() => {
    performance.mark('powerbi:load-start');
  }, []);

  const embedConfig = useMemo(
    () => ({
      type: 'report',
      embedUrl: report?.embedUrl,
      accessToken,
      id: report.id,
      tokenType: models.TokenType.Embed,
      settings: {
        panes: {
          filters: {
            visible: false,
          },
          pageNavigation: {
            position: models.PageNavigationPosition.Left,
          },
        },
      },
    }),
    [accessToken, report],
  );

  const eventHandlersMap = useMemo(
    () =>
      new Map([
        [
          'loaded',
          async function (event) {
            setShowPreqinLogo(true);
            performance.mark('powerbi:load-end');

            const { duration } = performance.measure('powerbi:load-time', 'powerbi:load-start', 'powerbi:load-end');

            console.log('Report data is loaded', duration);

            fireHeapEvent(
              `${workspaceName} - Report loaded`,
              {
                pageName: event?.detail?.page?.displayName,
                report: event?.detail?.report?.displayName,
                elapsedMs: duration,
              },
              workspaceName,
            );
          },
        ],
        [
          'rendered',
          () => {
            if (!isInitRenderEventSent.current) {
              performance.mark('powerbi:rendered');

              const { duration } = performance.measure('powerbi:render-time', 'powerbi:load-end', 'powerbi:rendered');

              console.log('Report rendered', duration);
              setShowPreqinLogo(false);

              fireHeapEvent(
                `${workspaceName} - Report rendered`,
                {
                  elapsedMs: duration,
                },
                workspaceName,
              );

              isInitRenderEventSent.current = true;
            }
          },
        ],
        [
          'selectionChanged',
          async function (event) {
            const visualData = await extractVisualDataWithReportDetails(event);
            fireHeapEvent(`${workspaceName} - Visual selection changed`, visualData, workspaceName);
          },
        ],
        [
          'visualClicked',
          async function (event) {
            const visualData = await extractVisualDataWithReportDetails(event);
            fireHeapEvent(`${workspaceName} - Visual clicked`, visualData, workspaceName);
          },
        ],
        [
          'error',
          function (event) {
            if (event) {
              const err = event.detail;

              console.error(err);
              console.log(`Error occurred: ${err.message}. Detailed message: ${err.detailedMessage}`);

              fireHeapEvent(
                `${workspaceName} - Error`,
                {
                  pageName: err?.page?.displayName,
                  report: err?.report?.displayName,
                  errorMessage: err?.message,
                  detailedMessage: err?.detailedMessage,
                },

                workspaceName,
              );

              rollbar.error(`PowerBI ${workspaceName} - Error`, new Error(err?.message), {
                detailedMessage: err?.detailedMessage,
                pageName: err?.page?.displayName,
                report: err?.report?.displayName,
              });
            }
          },
        ],
        [
          'pageChanged',
          (event) => {
            fireHeapEvent(
              `${workspaceName} - Page changed`,
              {
                pageName: event?.detail?.newPage?.displayName,
                report: event?.detail?.report?.displayName,
              },
              workspaceName,
            );
          },
        ],
      ]),
    [workspaceName],
  );

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {showPreqinLogo && workspacePath !== WorkspaceAppPath.InvestorTrends && (
        <div id="loading" className={styles.loading}>
          <PreqinLogo />
        </div>
      )}
      {showPreqinLogo && workspacePath === WorkspaceAppPath.InvestorTrends && (
        <div className={styles.basicLoader}>
          <AppLoader isLoading={showPreqinLogo} />
        </div>
      )}
      <PowerBIEmbed
        embedConfig={embedConfig}
        cssClassName={styles.report}
        eventHandlers={eventHandlersMap}
        getEmbeddedComponent={(embedObject: Embed) => {
          //@ts-ignore
          window.report = embedObject;
        }}
      />
    </div>
  );
};
